//@ts-check
import React, { useCallback, useRef, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { uploadPhoto } from 'api/photos'
import { useNotificationActions } from 'context/NotificationProvider'
import Button from 'components/buttons/Button'
import { H1 } from 'components/typography'
import DarkClassfy from 'assets/logos/main/DarkClassfy'
import ImageIcon from 'assets/icons/Image'
import {
  compressImage,
  checkImageQuality,
  REJECT_REASONS,
  QUALITY_STATUS
} from 'utils/files'
import styles from './UploadPhoto.module.css'

function UploadPhoto() {
  const [selectedFile, setSelectedFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [compressing, setCompressing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [badImage, setBadImage] = useState(false)
  const { setSuccessMessage, setErrorMessage, setWarningMessage } =
    useNotificationActions()

  const cameraInputRef = useRef(null)
  const galleryInputRef = useRef(null)
  const singleInputRef = useRef(null)
  const { studentId } = useParams()

  const clearImageData = useCallback(() => {
    setPreview(null)
    setSelectedFile(null)
    setBadImage(false)
    if (cameraInputRef.current) cameraInputRef.current.value = ''
    if (galleryInputRef.current) galleryInputRef.current.value = ''
    if (singleInputRef.current) singleInputRef.current.value = ''
  }, [])

  useEffect(() => {
    const checkIsMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera

      const hasTouchPoints = navigator.maxTouchPoints > 0
      const hasUserAgentMobile =
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent
        )
      const hasTouchEvents = 'ontouchstart' in window
      const isSmallScreen = window.innerWidth <= 768

      setIsMobile(
        hasTouchPoints || hasUserAgentMobile || hasTouchEvents || isSmallScreen
      )
    }

    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  const handleFileChange = async event => {
    const file = event.target?.files[0]
    if (!file) return
    setBadImage(false)
    try {
      if (!file.type.startsWith('image/'))
        throw new Error(
          `Archivo inválido "${
            file.type || '??'
          }". Carga una imagen válida (JPEG, PNG, GIF...).`
        )

      setCompressing(true)
      const { compressedFile, image } = await compressImage(file)

      const assessment = await checkImageQuality(image, compressedFile)

      if (assessment.status === QUALITY_STATUS.REJECT) {
        const { details } = assessment
        let errorMsg = 'Tu foto no cumple con los requisitos mínimos.'

        if (details.reason === REJECT_REASONS.LOW_RESOLUTION) {
          errorMsg = `La resolución es muy baja (${details.width}×${details.height}px). Usa una cámara de mejor calidad o acércate más al documento.`
        } else if (details.reason === REJECT_REASONS.MIN_FILE_SIZE) {
          errorMsg =
            'El archivo es demasiado pequeño. La imagen podría estar muy comprimida o ser de baja calidad.'
        } else if (details.reason === REJECT_REASONS.MAX_FILE_SIZE) {
          errorMsg = 'El archivo es demasiado grande (máx. 10MB).'
        } else if (!assessment.sharpnessOK) {
          errorMsg = `La imagen está desenfocada (nitidez: ${details.sharpness}). Asegúrate de que la cámara enfoque correctamente y que haya buena iluminación.`
        } else if (!assessment.contrastOK) {
          errorMsg = `La imagen tiene muy poco contraste (${details.contrast}). Mejora la iluminación`
        }

        setErrorMessage({
          title: 'Imagen de baja calidad',
          message: errorMsg,
          ms: 8000
        })
        setBadImage(true)
      }

      if (assessment.status === QUALITY_STATUS.WARN) {
        setWarningMessage({
          title: 'Calidad mejorable',
          message:
            'La foto podría no ser muy clara. Verifica que el texto se lea correctamente antes de subirla.',
          ms: 6000
        })
      }

      setSelectedFile(compressedFile)
      setPreview(image)
    } catch (error) {
      setErrorMessage({
        title: 'Error al procesar la imagen',
        message: error.message,
        ms: 12000
      })
      setWarningMessage({
        title: 'Información del archivo',
        message: (
          <>
            <ul>
              <li>{file.name}</li>
              <li>{file.type}</li>
              <li>{file.size}</li>
            </ul>
          </>
        ),
        ms: 10000
      })
    } finally {
      setCompressing(false)
    }
  }

  const handleSavingPhoto = useCallback(() => {
    setSaving(true)
    uploadPhoto({
      photo: selectedFile,
      studentId: studentId
    })
      .then(() => {
        setSuccessMessage({
          title: 'Imagen subida',
          message: 'Imagen subida correctamente'
        })
        clearImageData()
      })
      .catch(e => {
        setErrorMessage({ message: e.message })
        console.error('Error saving photo: ', e)
      })
      .finally(() => {
        setSaving(false)
      })
  }, [
    selectedFile,
    studentId,
    setSuccessMessage,
    setErrorMessage,
    clearImageData
  ])

  return (
    <section
      className={[styles.section, isMobile ? styles.mobileLayout : ''].join(
        ' '
      )}
    >
      <div className={styles.containerLogo}>
        <DarkClassfy className={styles.classfyLogo} />
      </div>
      <H1 className={styles.title}>Sube tus fotos 😎 </H1>

      <div className={styles.card}>
        <div
          className={[
            styles.imgContainer,
            badImage ? styles.badImage : ''
          ].join(' ')}
        >
          {preview ? (
            <div className={styles.previewContainer}>
              <img
                src={preview}
                alt='Imagen a subir'
                className={styles.image}
              />
              <div className={styles.previewOverlay}>
                <button
                  className={styles.removeButton}
                  onClick={clearImageData}
                  aria-label='Eliminar imagen'
                >
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.defaultContainer}>
              <ImageIcon
                color='var(--dadada)'
                className={styles.defaultImage}
              />
              <p className={styles.defaultText}>
                Comparte lo que has aprendido
              </p>
            </div>
          )}
        </div>
        <div className={styles.cardActions}>
          {isMobile ? (
            <div className={styles.mobileButtonsContainer}>
              <div className={styles.mobileButtonRow}>
                <Button
                  size='tiny'
                  type='secondary'
                  onClick={() => cameraInputRef.current.click()}
                  disabled={compressing || saving}
                >
                  📷 Cámara
                  <input
                    ref={cameraInputRef}
                    type='file'
                    accept='image/*'
                    capture='environment'
                    name='photo'
                    onChange={handleFileChange}
                    hidden
                  />
                </Button>
                <Button
                  size='tiny'
                  type='secondary'
                  onClick={() => galleryInputRef.current.click()}
                  disabled={compressing || saving}
                >
                  🖼️ Galería
                  <input
                    ref={galleryInputRef}
                    type='file'
                    accept='image/*'
                    name='photo'
                    onChange={handleFileChange}
                    hidden
                  />
                </Button>
              </div>
              <div className={styles.mobileButtonRow}>
                <Button
                  label='Subir'
                  size='tiny'
                  type='primary'
                  disabled={!selectedFile || saving || compressing || badImage}
                  loading={saving}
                  onClick={handleSavingPhoto}
                />
              </div>
            </div>
          ) : (
            <>
              <Button
                size='small'
                type='secondary'
                onClick={() => singleInputRef.current.click()}
                disabled={compressing || saving}
              >
                {compressing ? 'Procesando...' : 'Seleccionar'}
                <input
                  ref={singleInputRef}
                  type='file'
                  accept='image/*'
                  name='photo'
                  onChange={handleFileChange}
                  hidden
                />
              </Button>
              <Button
                label='Subir'
                size='small'
                type='primary'
                disabled={!selectedFile || saving || compressing || badImage}
                loading={saving}
                onClick={handleSavingPhoto}
              />
            </>
          )}
        </div>
      </div>
    </section>
  )
}

export default UploadPhoto
